// Prod Deploy Health widget — polls the top user-facing endpoints and shows
// live latency + status. Uses whatever REACT_APP_BACKEND_URL is baked into
// the current build, so:
//   • on preview → pings preview
//   • on prod    → pings prod
// Meaning a master admin can visit /admin on production, hit "Refresh", and
// spot regressions (say, a 20s /calls or a 502 /revivable-queue) in seconds
// without opening DevTools.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Activity, Loader2, RefreshCw, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The 8 endpoints most sensitive to prod-scale slowness. Kept small so a
// full sweep completes in a couple of seconds even on a warm cache miss.
const PROBES = [
  // pathTemplate matches the FastAPI route pattern that the middleware
  // records into the per-path ring buffer — used to look up p95 from
  // the live-p95 endpoint response.
  { key: 'notifications', method: 'GET', path: '/notifications/active', pathTemplate: '/api/notifications/active', budgetMs: 500 },
  { key: 'llm-health',    method: 'GET', path: '/admin/llm-health',     pathTemplate: '/api/admin/llm-health',     budgetMs: 500 },
  { key: 'calls-list',    method: 'GET', path: '/calls?limit=50',        pathTemplate: '/api/calls',                budgetMs: 1500 },
  { key: 'team-pulse',    method: 'GET', path: '/manager/team-pulse?days=7', pathTemplate: '/api/manager/team-pulse', budgetMs: 2000 },
  { key: 'coaching-queue', method: 'GET', path: '/analytics/coaching-queue', pathTemplate: '/api/analytics/coaching-queue', budgetMs: 2000 },
  { key: 'opportunity',   method: 'GET', path: '/analytics/opportunity-map?days=60&top_n=8', pathTemplate: '/api/analytics/opportunity-map', budgetMs: 3000 },
  { key: 'revivable',     method: 'GET', path: '/analytics/deal-outcomes/revivable-queue?days=30&limit=25', pathTemplate: '/api/analytics/deal-outcomes/revivable-queue', budgetMs: 2000 },
  { key: 'caller-intel',  method: 'GET', path: '/analytics/caller-intel',   pathTemplate: '/api/analytics/caller-intel',   budgetMs: 2000 },
];

const statusColour = (probe) => {
  if (probe.error || probe.status >= 500) return 'text-rose-400 bg-rose-500/10 border-rose-500/20';
  if (probe.status >= 400) return 'text-amber-400 bg-amber-500/10 border-amber-500/20';
  if (probe.elapsedMs > probe.budgetMs * 2) return 'text-rose-400 bg-rose-500/10 border-rose-500/20';
  if (probe.elapsedMs > probe.budgetMs) return 'text-amber-400 bg-amber-500/10 border-amber-500/20';
  return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
};

const StatusIcon = ({ probe }) => {
  if (probe.error || probe.status >= 500) return <XCircle className="w-3.5 h-3.5" />;
  if (probe.status >= 400 || probe.elapsedMs > probe.budgetMs) return <AlertTriangle className="w-3.5 h-3.5" />;
  return <CheckCircle2 className="w-3.5 h-3.5" />;
};

export const DeployHealthCard = () => {
  const { token } = useAuth();
  const [probes, setProbes] = useState(
    PROBES.map(p => ({ ...p, status: null, elapsedMs: null, error: null, p95: null, count: null }))
  );
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [windowSize, setWindowSize] = useState(100);
  const [tenantHotspots, setTenantHotspots] = useState([]);

  const run = useCallback(async () => {
    if (!token) return;
    setRunning(true);
    // Fire all probes in parallel — total wall-clock is bounded by the
    // slowest one. Each probe measures its own timing so cross-probe
    // contention doesn't skew the number.
    const results = await Promise.all(
      PROBES.map(async (p) => {
        const started = performance.now();
        try {
          const r = await axios.request({
            method: p.method,
            url: `${API}${p.path}`,
            headers: { Authorization: `Bearer ${token}` },
            timeout: 30000,
            validateStatus: () => true,  // don't throw on 4xx/5xx
          });
          return {
            ...p,
            status: r.status,
            elapsedMs: Math.round(performance.now() - started),
            error: null,
          };
        } catch (err) {
          return {
            ...p,
            status: null,
            elapsedMs: Math.round(performance.now() - started),
            error: err?.code || err?.message || 'network',
          };
        }
      })
    );

    // Now pull the per-path rolling p95 from the backend's in-memory
    // histogram (see /admin/perf/live-p95). We do this AFTER the burst
    // so the new samples we just generated are already reflected.
    let liveP95 = {};
    let ws = 100;
    try {
      const p95Res = await axios.get(`${API}/admin/perf/live-p95`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000,
        validateStatus: () => true,
      });
      if (p95Res.status === 200) {
        liveP95 = p95Res.data?.paths || {};
        ws = p95Res.data?.window_size || 100;
        setTenantHotspots(p95Res.data?.tenant_hotspots || []);
      }
    } catch {
      // Silent — the widget still renders the single-shot ping without p95.
      setTenantHotspots([]);
    }

    setProbes(results.map(p => {
      const stats = liveP95[p.pathTemplate] || null;
      return {
        ...p,
        p95: stats?.p95_ms ?? null,
        count: stats?.count ?? null,
      };
    }));
    setWindowSize(ws);
    setLastRun(new Date());
    setRunning(false);
  }, [token]);

  useEffect(() => { run(); }, [run]);

  return (
    <div
      className="bg-white/5 border border-white/10 rounded-xl p-5"
      data-testid="deploy-health-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-teal-400" />
          <h3 className="text-sm font-semibold text-white/90">Deploy Health</h3>
          <span className="text-white/30 text-xs">
            {lastRun ? `checked ${lastRun.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}` : ''}
          </span>
        </div>
        <button
          onClick={run}
          disabled={running}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-xs text-white/70 disabled:opacity-40 transition-colors"
          data-testid="deploy-health-refresh"
        >
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {probes.map((p) => {
          // Colour the p95 chip against the same budget as the single-shot
          // ping: green if under budget, amber up to 2×, red past that.
          // Only meaningful when we have at least a handful of samples.
          const hasP95 = p.p95 != null && p.count >= 5;
          const p95Colour = !hasP95 ? 'text-white/30' :
            p.p95 > p.budgetMs * 2 ? 'text-rose-300' :
            p.p95 > p.budgetMs ? 'text-amber-300' :
            'text-emerald-300/80';
          return (
            <div
              key={p.key}
              className={`px-3 py-2 rounded-lg border ${statusColour(p)} flex flex-col gap-1 text-xs font-mono`}
              title={`${p.method} ${p.path}\nbudget ${p.budgetMs}ms${p.error ? ` — ${p.error}` : ''}${hasP95 ? `\np50/p95/p99 across last ${p.count} requests` : ''}`}
              data-testid={`deploy-probe-${p.key}`}
            >
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 truncate">
                  <StatusIcon probe={p} />
                  <span className="truncate">{p.key}</span>
                </span>
                <span className="tabular-nums">
                  {p.elapsedMs != null ? `${p.elapsedMs}ms` : '—'}
                  {p.status != null && p.status !== 200 && (
                    <span className="ml-1 opacity-80">·{p.status}</span>
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between text-[10px] tabular-nums opacity-80">
                <span className="opacity-70">p95</span>
                <span className={p95Colour} data-testid={`deploy-probe-p95-${p.key}`}>
                  {hasP95 ? `${p.p95}ms · n=${p.count}` : (p.count != null ? `n=${p.count}` : 'n=0')}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-white/30 text-[10px] mt-3">
        Pings the deployed backend at <span className="font-mono">{process.env.REACT_APP_BACKEND_URL}</span>.
        Green single-shot = under budget · Amber = 1–2× · Red = &gt;2× or 5xx / timeout.
        <span className="ml-1 opacity-80">p95 across last {windowSize} in-flight requests per path (in-memory, per pod).</span>
      </p>
      {tenantHotspots.length > 0 && (
        <div className="mt-3 pt-3 border-t border-white/10" data-testid="tenant-hotspots">
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-white/50">
              Noisy neighbours
            </span>
            <span className="text-[9px] text-white/30">
              (top-5 slowest (path × tenant) buckets · n≥5 samples)
            </span>
          </div>
          <ul className="space-y-1">
            {tenantHotspots.map((h, i) => {
              const shortPath = h.path.replace(/^\/api/, '');
              return (
                <li
                  key={`${h.path}-${h.domain_id}`}
                  className="flex items-center justify-between text-[10px] font-mono text-white/70"
                  data-testid={`tenant-hotspot-${i}`}
                >
                  <span className="truncate flex-1 mr-2">
                    <span className="text-teal-300/70">{h.domain_name}</span>
                    <span className="text-white/40"> · </span>
                    <span>{shortPath}</span>
                  </span>
                  <span className="tabular-nums shrink-0">
                    <span className="text-rose-300">{h.p95_ms}ms</span>
                    <span className="text-white/30"> p95 · n={h.count}</span>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DeployHealthCard;
